type TrialAccount = { nr?: unknown; name?: unknown; category?: unknown; ut?: unknown }
type CarryForwardContext = { floorYear: number; closingTotalsByYear: Map<number, Map<string, TrialAccount>> }

const trialFiscalBounds = (settings: any, year: number) => {
  const firstFrom = String(settings?.firstFiscalYearStart || '').slice(0, 10)
  const firstTo = String(settings?.firstFiscalYearEnd || '').slice(0, 10)
  if (/^\d{4}-\d{2}-\d{2}$/.test(firstFrom) && /^\d{4}-\d{2}-\d{2}$/.test(firstTo)
    && Number(firstTo.slice(0, 4)) === Number(year)) return { from: firstFrom, to: firstTo }
  const declared = Number(settings?.fiscalYearStartMonth)
  const month = Number.isInteger(declared) && declared >= 1 && declared <= 12 ? declared : 1
  const startYear = month === 1 ? Number(year) : Number(year) - 1
  return { from: `${startYear}-${String(month).padStart(2, '0')}-01`,
    to: new Date(Date.UTC(startYear + 1, month - 1, 0)).toISOString().slice(0, 10) }
}

const list = (value: unknown): any[] => Array.isArray(value) ? value : []
const yearFrom = (value: unknown): number | null => {
  const raw = String(value || '').trim()
  const year = Number(raw.match(/^(\d{4})/)?.[1] || raw.match(/^\d{2}\.\d{2}\.(\d{4})/)?.[1])
  return Number.isInteger(year) && year >= 1900 && year <= 2200 ? year : null
}
const createContext = (state: any, fallbackYear: number): CarryForwardContext => {
  const years: unknown[] = [state?.settings?.startYear]
    .concat(Object.keys(state?.openingBalanceByYear || {}))
    .concat(Object.keys(state?.accountingImportReportStatsByYear || {}))
    .concat(Object.keys(state?.yearEndEntriesByYear || {}))
    .concat(Object.keys(state?.payrollTaxPaymentsByYear || {}))
  ;[state?.invoices, state?.otherSales, state?.expenses, state?.customsDeclarations, state?.payments, state?.payrollRuns, state?.bankReconciliationPostings]
    .forEach((entries) => list(entries).forEach((entry) => years.push(entry?.date || entry?.paymentDate || entry?.period || entry?.createdAt)))
  list(state?.accountingEvents).forEach((event) => years.push(event.accounting_date))
  const validYears = years.map(yearFrom).filter((value): value is number => value !== null)
  // A loop, not a spread: one year per journal event overflows the call stack on large journals.
  let floorYear = Number(fallbackYear)
  if (validYears.length) { floorYear = validYears[0]; for (const year of validYears) if (year < floorYear) floorYear = year }
  return { floorYear, closingTotalsByYear: new Map() }
}
const hasExplicitOpening = (state: any, year: number): boolean =>
  Boolean(state?.accountingImportReportStatsByYear?.[String(year)]) ||
  Object.prototype.hasOwnProperty.call(state?.openingBalanceByYear || {}, String(year)) ||
  list(state?.yearEndEntriesByYear?.[String(year)]).some((entry) => entry?.kind === 'opening_balance')
const hasJournalOpening = (state: any, year: number): boolean => list(state?.accountingEvents)
  .some((event) => event.source_type === 'openingBalance' && yearFrom(event.accounting_date) === Number(year))
const isBalanceAccount = (account: TrialAccount): boolean => {
  if (account?.category) return account.category === 'asset' || account.category === 'equity-liability'
  const numeric = Number(String(account?.nr || '').match(/^(\d{4,5})/)?.[1])
  return numeric >= 1000 && numeric <= 2999
}
const addPreviousClosingBalance = ({ year, context, buildClosingTotals, addAccount }: any): void => {
  const previousYear = Number(year) - 1
  if (previousYear < context.floorYear) return
  let totals = context.closingTotalsByYear.get(previousYear)
  if (!totals) {
    totals = buildClosingTotals(previousYear)
    context.closingTotalsByYear.set(previousYear, totals)
  }
  // Carry booked balances only; allocating an unclosed result would invent an equity posting.
  totals.forEach((account: TrialAccount) => { if (isBalanceAccount(account)) addAccount(account) })
}

const fiscalTrial = ({ state, year, options, buildTotals, groups }: any) => {
  const bounds = trialFiscalBounds(state.settings, year)
  const [startYear, startMonth] = bounds.from.split('-').map(Number)
  const dateAt = (month: number, day: number) => new Date(Date.UTC(startYear, startMonth - 1 + month, day)).toISOString().slice(0, 10)
  const monthCount = (Number(bounds.to.slice(0, 4)) - startYear) * 12 + Number(bounds.to.slice(5, 7)) - startMonth + 1
  const months = Array.isArray(options.months) ? options.months : [0, monthCount - 1]
  const from = options.from || dateAt(months[0], 1)
  const to = options.to || dateAt(months[1] + 1, 0)
  const openingTo = new Date(Date.parse(`${from}T00:00:00Z`) - 86400000).toISOString().slice(0, 10)
  const common = { fiscalYear: true, projectId: options.projectId, departmentId: options.departmentId }
  const opening = buildTotals(state, year, { ...common, from: bounds.from, to: openingTo,
    months: from === bounds.from ? [0, -1] : [0, monthCount - 1] },
    { includeOpeningBalance: true, carryForward: createContext(state, year) })
  const movement = buildTotals(state, year, { ...common, from, to, months }, { includeOpeningBalance: false })
  const rounded = (value: any) => Math.round(Number(value || 0) * 100) / 100
  const accounts = [...new Set<string>([...opening.keys(), ...movement.keys()])].map(code => {
    const row = opening.get(code) || movement.get(code)
    const inn = rounded(opening.get(code)?.ut), endring = rounded(movement.get(code)?.ut)
    return { ...row, inn, endring, ut: rounded(inn + endring) }
  }).sort((a, b) => String(a.nr).localeCompare(String(b.nr)))
  return groups.map((group: any) => ({ key: group.key, labelKey: group.labelKey,
    accounts: accounts.filter((account: any) => group.match(account)) }))
}

const trialBalanceContinuityApi = { createContext, hasExplicitOpening, hasJournalOpening, addPreviousClosingBalance, fiscalBounds: trialFiscalBounds, fiscalTrial }
window.PlummeTrialBalanceContinuity = trialBalanceContinuityApi
